import { APP_DISCLAIMER, APP_WARNING_NOTICE } from '@/constants/config';
import { AuthLinks } from '@/components/Auth';
import { Button } from '@/components/Buttons';
import { FieldOneLogoSvg } from '@/components/Icons';
import { useGlobalStore } from '@/store/global-store';

/**
 * UsageConsentModal component displays a modal dialog that informs users
 * about the usage consent requirements and requires acceptance before proceeding.
 */
export const UsageConsentModal: React.FC = () => {
  const { setUsageConsentAccepted } = useGlobalStore();

  const handleAccept = () => {
    setUsageConsentAccepted(true);
    sessionStorage.setItem('consentAccepted', 'true');
  };

  return (
    <div className="my-12 flex flex-col items-center justify-center gap-6 md:mt-16 md:gap-8 lg:mt-20 lg:gap-10">
      <div className="flex w-full items-center justify-center">
        <FieldOneLogoSvg />
      </div>

      <div className="w-full max-w-[536px] rounded-lg bg-neutral-white p-8 shadow-md">
        <div className="text-center">
          <h1 className="font-display text-xl font-bold text-semantic-text-secondary">
            AI Platform Usage Warning and Consent
          </h1>
        </div>

        <div className="mt-6 flex flex-col items-start gap-4 text-base font-bold">
          <p>
            The role-play scenarios were designed with the assistance of an AI language model. All
            content was reviewed and approved by the creators, who remain responsible for the
            integrity and accuracy of the work. Please use discretion when interpreting the
            responses.
          </p>
          <p>
            This platform is provided for internal training and educational simulation only. It is
            not intended for promotional use, customer interaction, or external distribution.
            Platform outputs are hypothetical and may be incomplete or inaccurate—always verify
            against approved resources when interacting with the platform.
          </p>
          <p>
            Use only approved assets and messaging; adhere to all compliance guardrails, J&J
            standards, and applicable laws/regulations.
          </p>
          <p>
            Do not discuss or imply off-label uses. Maintain fair balance and align strictly with
            approved labeling and claims.
          </p>
          <p>
            Outputs are for simulation; they are not medical, legal, regulatory, or policy advice.
            Consult approved materials or the appropriate teams for guidance.
          </p>
        </div>

        <div className="mt-4 flex items-center justify-center p-4">
          <Button label="Accept" onClick={handleAccept} variant="primary" />
        </div>

        <div className="p-4 text-center">
          <AuthLinks />
        </div>

        <div className="flex flex-col gap-4 p-0 text-center text-xs text-semantic-text-secondary">
          <p>{APP_WARNING_NOTICE}</p>
          <p className="font-bold">{APP_DISCLAIMER}</p>
        </div>
      </div>
    </div>
  );
};

UsageConsentModal.displayName = 'UsageConsentModal';

export default UsageConsentModal;
